import React, { useState } from 'react';

import { ButtonGroup, Button } from '@mui/material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import {
  OrdReferenceInput,
  OrdBulkUpdateFormButton,
  OrdFormList,
  useFormType,
  OrdSimpleForm,
  LabelsInput,
  OrdIconStandard,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { AgentFields, AgentSettingsFields, dataSchema } from '@ragu-components/ord-types';
import { canAccessWithPermissions } from '@ratto-components/ra-rbac';
import { TextInput, usePermissions, useRecordContext, useTranslate } from 'react-admin';

import { AgentQueuesList } from './AgentQueuesList';

import type { OrdSimpleFormProps } from '@ragu-components/ord-components';
import type { Agent } from '@ragu-components/ord-types';

/**
 * Renders the AgentsForm component.
 *
 * @param {OrdTabbedFormProps} props - The props for the AgentsForm component.
 * @return {React.ReactElement} The rendered AgentsForm component.
 */
export const AgentsForm = ({ ...props }: OrdSimpleFormProps): React.ReactElement => {
  const { create } = useFormType();
  const record = useRecordContext<Agent>();
  const translate = useTranslate();
  const [userMode, setUserMode] = useState<boolean>(false);
  const { permissions } = usePermissions();
  const canShowAgentQueues = canAccessWithPermissions({
    permissions,
    action: 'list',
    resource: Resources.agents,
  });

  React.useEffect(() => {
    if (record?.user != null) {
      setUserMode(true);
    } else if (record?.group != null) {
      setUserMode(false);
    }
  }, [record]);

  return (
    <OrdSimpleForm
      sanitizeFields
      replaceRelationships
      createJsonSchema={userMode ? dataSchema.AgentUser : dataSchema.AgentGroup}
      editJsonSchema={userMode ? dataSchema.AgentUserUpdate : dataSchema.AgentGroupUpdate}
      validatorOptions={create ? { removeAdditional: 'failing' } : undefined}
      deleteProps={{ redirect: false }}
      {...props}
    >
      {create && (
        <>
          <OrdFormList icon={<OrdIconStandard name='edit' />}>
            <TextInput source={AgentFields.name} isRequired resettable={create} autoComplete='off' />
          </OrdFormList>
          <OrdFormList icon={<OrdIconStandard name='numbers' />}>
            <OrdReferenceInput resource={Resources.queues} isRequired source={AgentFields.queue} />
            <ButtonGroup disableElevation variant='contained' aria-label='Disabled button group'>
              <Button
                variant={!userMode ? 'contained' : 'outlined'}
                onClick={() => {
                  setUserMode(false);
                }}
              >
                {translate('resources.agents.fields.group')}
              </Button>
              <Button
                variant={userMode ? 'contained' : 'outlined'}
                onClick={() => {
                  setUserMode(true);
                }}
              >
                {translate('resources.agents.fields.user')}
              </Button>
            </ButtonGroup>
            {userMode ? (
              <OrdReferenceInput resource={Resources.users} source={AgentFields.user} />
            ) : (
              <OrdReferenceInput resource={Resources.groups} source={AgentFields.group} />
            )}
          </OrdFormList>
        </>
      )}
      <OrdFormList icon={<OrdIconStandard name='settings' />}>
        <JsonSchemaInput source={AgentFields.settings} />
      </OrdFormList>
      <OrdFormList icon={<OrdIconStandard name='labels' />}>
        <LabelsInput />
      </OrdFormList>
      {!create && canShowAgentQueues && record?.user != null && (
        <OrdFormList icon={<OrdIconStandard name='numbers' />}>
          <AgentQueuesList
            filterField='user.id'
            filterValue={record.user.id}
            excludeAgentId={record.id}
            title={translate('resources.agents.messages.other_queues_for_user', {
              _: "This user's other queues",
            })}
          />
        </OrdFormList>
      )}
      {!create && canShowAgentQueues && record?.group != null && (
        <OrdFormList icon={<OrdIconStandard name='numbers' />}>
          <AgentQueuesList
            filterField='group.id'
            filterValue={record.group.id}
            excludeAgentId={record.id}
            title={translate('resources.agents.messages.other_queues_for_group', {
              _: "This group's other queues",
            })}
          />
        </OrdFormList>
      )}
    </OrdSimpleForm>
  );
};

/** Form for bulk editing agents */
export const AgentsBulkEditForm = (): React.ReactElement => {
  const jsonSchema = dataSchema.AgentSettings;
  const inputs = [
    {
      label: 'resources.agents.fields.queue',
      element: (
        <OrdFormList icon={<OrdIconStandard name='numbers' />}>
          <OrdReferenceInput resource={Resources.queues} isRequired source={AgentFields.queue} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.agents.fields.settings.busy_delay_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={AgentSettingsFields.busy_delay_time}
            source={`settings.${AgentSettingsFields.busy_delay_time}`}
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.agents.fields.settings.call_timeout',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={AgentSettingsFields.call_timeout}
            source={`settings.${AgentSettingsFields.call_timeout}`}
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.agents.fields.settings.no_answer_delay_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={AgentSettingsFields.no_answer_delay_time}
            source={`settings.${AgentSettingsFields.no_answer_delay_time}`}
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.agents.fields.settings.position',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={AgentSettingsFields.position}
            source={`settings.${AgentSettingsFields.position}`}
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.agents.fields.settings.reject_delay_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={AgentSettingsFields.reject_delay_time}
            source={`settings.${AgentSettingsFields.reject_delay_time}`}
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.agents.fields.settings.wrap_up_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={AgentSettingsFields.wrap_up_time}
            source={`settings.${AgentSettingsFields.wrap_up_time}`}
          />
        </OrdFormList>
      ),
    },
  ];
  return <OrdBulkUpdateFormButton inputs={inputs} />;
};
